import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { ShiftManagementRolesRequirementsFragment } from 'generated/resources/graphql'
import { Moment } from 'moment'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

import Content from './Content'
import useUnassignedCustomShiftForm from './useUnassignedCustomShiftForm'
import useUnassignedDefaultShiftForms from './useUnassignedDefaultShiftForms'
import { ShiftType, ShiftTypeOptions } from './utils'

interface Props {
  date: Moment
  onClose: () => void
  roles: ShiftManagementRolesRequirementsFragment[]
}

const AddUnassignedShiftModal = ({ date, onClose, roles }: Props) => {
  const [shiftType, setShiftType] = React.useState<ShiftType>(ShiftTypeOptions.default)
  const isDefaultShift = shiftType === ShiftTypeOptions.default

  const { unassignedShiftForm, useSubmitUnassignedCustomShift } = useUnassignedCustomShiftForm({
    date,
  })
  const { onSubmit, submitting } = useSubmitUnassignedCustomShift()
  const startDate = unassignedShiftForm.get('start_date').value
  const endDate = unassignedShiftForm.get('end_date').value
  const staffRequirements = unassignedShiftForm.get('staff_requirements').value
  const counters = staffRequirements.map(
    (requirement: { staff_count: number }) => requirement.staff_count
  )
  const minCounter = Math.min(...counters)

  const onSelectShiftType = (type: ShiftType) => {
    const newShiftType = shiftType === type ? undefined : type
    setShiftType(newShiftType)
  }

  const { onToggleShift, forms, useSubmitUnassignedDefaultShifts } =
    useUnassignedDefaultShiftForms()
  const { onSubmit: onSubmitDefault, submitting: submittingDefault } =
    useSubmitUnassignedDefaultShifts({
      startDate: moment(startDate),
      endDate: moment(endDate),
      staffRequirements,
    })

  const handleOnSubmit = isDefaultShift ? onSubmitDefault : onSubmit

  const emptyParams = !(isDefaultShift
    ? forms.some((f) => f.isSelected)
    : unassignedShiftForm.isValid)

  const hasValidLocation = (form: UnassignedShiftForm) => {
    if (!form.get('all_company').value) {
      return (
        form.get('locations_ids_visibilities').value !== '' && form.get('location_id').value !== ''
      )
    }
    return form.get('location_id').value !== ''
  }

  const isWorkplaceSelected = isDefaultShift
    ? forms.filter((f) => f.isSelected).every(hasValidLocation)
    : hasValidLocation(unassignedShiftForm)

  const disableSubmit =
    minCounter === 0 || emptyParams || submitting || submittingDefault || !isWorkplaceSelected

  return (
    <Modal
      illustration="tasks_management"
      title={i18n.t('shift_management.coverage_section.unassigned_shift.title')}
      description={i18n.t('shift_management.coverage_section.unassigned_shift.description')}
      onSubmit={handleOnSubmit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disableSubmit} submit>
            {i18n.t('common.add')}
          </Button>
        </>
      }
    >
      <Content
        unassignedDefaultShiftsForms={forms}
        unassignedShiftForm={unassignedShiftForm}
        shiftType={shiftType}
        onToggleShift={onToggleShift}
        onSelectShiftType={onSelectShiftType}
        roles={roles}
      />
    </Modal>
  )
}

export default observer(AddUnassignedShiftModal)
